/**
 * AdminAuditLogTab Component
 * Displays admin audit logs with filtering, search, and pagination.
 *
 * Features:
 * - Filterable table of admin actions
 * - Filter row: action type, resource type, date range, search
 * - Paginated results (50 per page)
 * - Expandable details column for JSON data
 */
import { h } from 'preact';
import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { formatDateTime, formatTimeAgo } from '../../utils/dateUtils.js';
import { getAuditLogs, getAuditLogActions } from '../../services/adminService.js';

// Human-readable action labels
const ACTION_LABELS = {
  approve_subscription: 'Approve Subscription',
  decline_subscription: 'Decline Subscription',
  revoke_subscription: 'Revoke Subscription',
  modify_subscription: 'Modify Subscription',
  grant_subscription: 'Grant Subscription',
  reactivate_subscription: 'Reactivate Subscription',
  review_quote: 'Review Quote',
  confirm_payment: 'Confirm Payment',
  send_reminder: 'Send Reminder',
  delete_user: 'Delete User',
  update_user: 'Update User',
  clear_cache: 'Clear Cache',
  create_promo_code: 'Create Promo Code',
  update_promo_code: 'Update Promo Code',
  delete_promo_code: 'Delete Promo Code',
};

function getActionLabel(action) {
  return ACTION_LABELS[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Action badge color mapping
function getActionBadgeClass(action) {
  if (!action) return 'admin-badge--basic';
  if (action.includes('approve') || action.includes('grant') || action.includes('reactivate') || action.includes('confirm')) {
    return 'admin-badge--success';
  }
  if (action.includes('decline') || action.includes('revoke') || action.includes('delete')) {
    return 'admin-badge--error';
  }
  if (action.includes('modify') || action.includes('update') || action.includes('review')) {
    return 'admin-badge--warning';
  }
  return 'admin-badge--info';
}

export function AdminAuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Filters
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    search: '',
    start_date: '',
    end_date: ''
  });

  // Filter options (loaded from API)
  const [actionOptions, setActionOptions] = useState([]);
  const [resourceTypeOptions, setResourceTypeOptions] = useState([]);

  // Expanded detail rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  const searchTimeout = useRef(null);

  // Load filter options on mount
  useEffect(() => {
    getAuditLogActions()
      .then(data => {
        setActionOptions(data.actions || []);
        setResourceTypeOptions(data.resource_types || []);
      })
      .catch(() => {});
  }, []);

  // Load audit logs
  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = { limit, offset };
      if (filters.action) params.action = filters.action;
      if (filters.resource_type) params.resource_type = filters.resource_type;
      if (filters.search) params.search = filters.search;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const data = await getAuditLogs(params);
      setLogs(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('[AdminAuditLog] Load error:', err);
      setError(err?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSearchChange = (value) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: value }));
      setOffset(0);
    }, 300);
  };

  const toggleRowExpand = (logId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  const clearFilters = () => {
    setFilters({ action: '', resource_type: '', search: '', start_date: '', end_date: '' });
    setOffset(0);
  };

  const hasActiveFilters = filters.action || filters.resource_type || filters.search || filters.start_date || filters.end_date;

  if (loading && logs.length === 0) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading audit logs...</p>
      </div>
    );
  }

  return (
    <div className="admin-tab-content">
      {/* Header */}
      <div className="admin-section-header">
        <div className="admin-section-title">
          <h2>Audit Log</h2>
          <span className="admin-section-count">{total} entries</span>
        </div>
        <button className="admin-btn admin-btn--secondary" onClick={loadLogs} disabled={loading}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
          </svg>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Filter bar */}
      <div className="admin-filter-bar">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search actions, details, resource IDs..."
            onInput={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        <select
          className="admin-filter-select"
          value={filters.action}
          onChange={(e) => { setFilters(prev => ({ ...prev, action: e.target.value })); setOffset(0); }}
        >
          <option value="">All Actions</option>
          {actionOptions.map(a => (
            <option key={a} value={a}>{getActionLabel(a)}</option>
          ))}
        </select>

        <select
          className="admin-filter-select"
          value={filters.resource_type}
          onChange={(e) => { setFilters(prev => ({ ...prev, resource_type: e.target.value })); setOffset(0); }}
        >
          <option value="">All Resources</option>
          {resourceTypeOptions.map(rt => (
            <option key={rt} value={rt}>{rt.charAt(0).toUpperCase() + rt.slice(1)}</option>
          ))}
        </select>

        <input
          type="date"
          className="admin-filter-select"
          value={filters.start_date}
          onChange={(e) => { setFilters(prev => ({ ...prev, start_date: e.target.value })); setOffset(0); }}
          title="Start date"
          style={{ minWidth: '130px' }}
        />
        <input
          type="date"
          className="admin-filter-select"
          value={filters.end_date}
          onChange={(e) => { setFilters(prev => ({ ...prev, end_date: e.target.value })); setOffset(0); }}
          title="End date"
          style={{ minWidth: '130px' }}
        />

        {hasActiveFilters && (
          <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={clearFilters}>
            Clear
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="admin-alert admin-alert--error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M15 9l-6 6M9 9l6 6"/>
          </svg>
          {error}
          <button className="admin-alert-close" onClick={() => setError('')}>x</button>
        </div>
      )}

      {/* Empty state */}
      {logs.length === 0 && !loading ? (
        <div className="admin-empty-state admin-empty-state--glass">
          <div className="admin-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
          </div>
          <h3>No audit log entries found</h3>
          <p>Try adjusting your filters or search query</p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Resource ID</th>
                  <th>Details</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const isExpanded = expandedRows.has(log.id);
                  return (
                    <>
                      <tr key={log.id}>
                        <td>
                          <span title={formatDateTime(log.created_at)}>
                            {formatTimeAgo(log.created_at)}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.85rem' }}>
                            <div>{log.admin_name || 'Unknown'}</div>
                            <div style={{ color: 'var(--text-secondary, #aaa)', fontSize: '0.78rem' }}>{log.admin_email || '-'}</div>
                          </div>
                        </td>
                        <td>
                          <span className={`admin-badge ${getActionBadgeClass(log.action)}`}>
                            {getActionLabel(log.action)}
                          </span>
                        </td>
                        <td>
                          <span className="admin-badge admin-badge--basic">
                            {log.resource_type || '-'}
                          </span>
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                          {log.resource_id || '-'}
                        </td>
                        <td>
                          {log.details ? (
                            <button
                              className="admin-btn admin-btn--secondary admin-btn--sm"
                              onClick={() => toggleRowExpand(log.id)}
                              style={{ fontSize: '0.78rem', padding: '2px 8px' }}
                            >
                              {isExpanded ? 'Hide' : 'View'}
                            </button>
                          ) : (
                            <span style={{ color: 'var(--text-secondary, #aaa)', fontSize: '0.82rem' }}>-</span>
                          )}
                        </td>
                        <td style={{ fontSize: '0.78rem', color: 'var(--text-secondary, #aaa)' }}>
                          {log.ip_address || '-'}
                        </td>
                      </tr>
                      {isExpanded && log.details && (
                        <tr key={`${log.id}-details`} style={{ background: 'var(--bg-tertiary, #1a1a2e)' }}>
                          <td colSpan="7" style={{ padding: '12px 16px' }}>
                            <pre style={{
                              margin: 0,
                              fontSize: '0.8rem',
                              lineHeight: '1.5',
                              color: 'var(--text-primary, #e0e0e0)',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxHeight: '300px',
                              overflowY: 'auto',
                              background: 'var(--bg-primary, #0d0d1a)',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid var(--border-color, #333)'
                            }}>
                              {typeof log.details === 'object'
                                ? JSON.stringify(log.details, null, 2)
                                : String(log.details)
                              }
                            </pre>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="admin-pagination">
              <button
                className="admin-btn admin-btn--secondary admin-btn--sm"
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
              >
                Previous
              </button>
              <span className="admin-pagination-info">
                Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
              </span>
              <button
                className="admin-btn admin-btn--secondary admin-btn--sm"
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
